import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { getAlbum, getAlbums } from "../../Album/api/album.services.ts"
import { getAttachments } from "../../Album/api/attachment.services.ts"
import { type AlbumListItem } from "../../Album/types/album.types.ts"
import { type Attachment } from "../../Album/types/attachment.types.ts"
import { getProjectDocuments } from "../../Project/api/project-documents.services.ts"
import { type ProjectDocument } from "../../Project/types/project-documents.types.ts"
import { attachmentMatchesFilters } from "../helpers/attachment-matches-filters.ts"
import {
  documentMatchesFilters,
  documentsCanMatchFilters
} from "../helpers/document-matches-filters.ts"
import { resolveDateBounds } from "../helpers/date-range-filter.ts"
import { collectFolderIds } from "../helpers/folder-tree.ts"
import {
  openFolderPath,
  ROOT_FOLDER_PATH,
  truncateFolderPath,
  withAncestors
} from "../helpers/folder-path.ts"
import { listEveryFolder } from "../helpers/list-every-folder.ts"
import { resolveFolderAncestry } from "../helpers/resolve-folder-breadcrumb.ts"
import {
  mapAttachmentToFileItem,
  mapDocumentToFileItem
} from "../helpers/map-to-file-item.ts"
import { documentOrderingFor } from "../helpers/server-ordering.ts"
import { type FileItem, type FileRights } from "../types/file-item.types.ts"
import {
  countActiveFilters,
  DEFAULT_SORT_ORDER,
  EMPTY_FILES_FILTERS,
  type FilesFilters,
  type FilesSortOrder
} from "../types/files-filters.types.ts"
import { type FilesNav } from "../types/files-nav.types.ts"
import { type FolderListItem } from "../types/folder.types.ts"

export const ALBUMS_PREVIEW_LIMIT = 6
export const DOCUMENT_PAGE_SIZE = 100

const ROOT_FOLDER_KEY = "root"
const folderKey = (folderId: string | null) => folderId ?? ROOT_FOLDER_KEY

const NAVS_WITHOUT_LISTING: FilesNav[] = ["albums", "trash"]

// `file_type` (a MIME-category bucket, not a stored column), `owner`
// (matched by display name today, not a user id the server could filter
// on), `album` membership, and the subtree half of "include subfolders"
// have no server equivalent yet — those stay in *-matches-filters.ts,
// applied over whichever page this sends back. Everything else here is a
// real server-side query param, so it narrows the query itself rather than
// the page already sitting in the browser.
// Shared across all four endpoints (documents, photos, folders, albums).
const dateAndCustomerParams = (filters: FilesFilters) => {
  const { after, before } = resolveDateBounds(filters)
  const params: Record<string, string> = {}
  if (after) params.created_after = after
  if (before) params.created_before = before
  if (filters.customers.length > 0) params.customer = filters.customers.join(",")
  return params
}

const sharedFilterParams = (filters: FilesFilters) => {
  const params = dateAndCustomerParams(filters)
  // "Include subfolders" expands to a whole id set client-side (see
  // `foldersForFilter` below) — only the exact-folder case is a single id
  // the server can filter on directly.
  if (filters.folder && !filters.folder_include_subfolders) {
    params.folder = filters.folder
  }
  // Origin here is who UPLOADED the file — the container (folder/album)
  // half of "Uploaded by"/"Created by" is a separate control, see
  // `containerFilterParams`.
  if (filters.uploaded_by_origin) params.uploaded_by_origin = filters.uploaded_by_origin
  return params
}

// Containers (folders/albums) — origin is who CREATED the container, a
// separate question from what it currently holds (a team-created album
// full of customer uploads is not itself customer-created).
const containerFilterParams = (filters: FilesFilters) => {
  const params = dateAndCustomerParams(filters)
  if (filters.created_by_origin) params.created_by_origin = filters.created_by_origin
  return params
}

const documentFilterParams = (filters: FilesFilters) => {
  const params = sharedFilterParams(filters)
  if (filters.document_type) params.document_type = filters.document_type
  if (filters.status) params.status = filters.status
  if (filters.related_record_type && filters.related_record_id) {
    params.related_record_type = filters.related_record_type
    params.related_record_id = filters.related_record_id
  }
  return params
}

const attachmentFilterParams = (filters: FilesFilters) => {
  const params = sharedFilterParams(filters)
  if (filters.model_type) params.model_type = filters.model_type
  if (filters.document_type) params.document_type = filters.document_type
  if (filters.status) params.status = filters.status
  return params
}

const hasActiveFilter = (filters: FilesFilters) => countActiveFilters(filters) > 0

interface UseFilesDataArgs {
  // Omitted entirely means the company workspace (Phase C1) — every call
  // below sends `location: "company"` instead of `project`/`lead`. Albums
  // have no company-scope equivalent yet (a later phase), so the Albums
  // fetch is always skipped in that mode.
  projectId?: string
  initialNav: FilesNav
  rights: FileRights
  sort?: FilesSortOrder
}

export function useFilesData({
  projectId,
  initialNav,
  rights,
  sort = DEFAULT_SORT_ORDER
}: UseFilesDataArgs) {
  const [nav, setNavState] = useState<FilesNav>(initialNav)
  const [folderPath, setFolderPath] = useState(ROOT_FOLDER_PATH)
  const [search, setSearch] = useState("")
  const [filters, setFilters] = useState<FilesFilters>(EMPTY_FILES_FILTERS)

  const [attachments, setAttachments] = useState<Attachment[]>([])
  const [documents, setDocuments] = useState<ProjectDocument[]>([])
  const [documentsTotal, setDocumentsTotal] = useState(0)
  const [documentsPage, setDocumentsPage] = useState(1)
  const [folders, setFolders] = useState<FolderListItem[]>([])
  const [albumsPreview, setAlbumsPreview] = useState<AlbumListItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [albumAttachmentIds, setAlbumAttachmentIds] = useState<Set<
    string
  > | null>(null)

  const [listedFolderKey, setListedFolderKey] = useState<string | null>(null)

  const requestId = useRef(0)
  const documentOrdering = documentOrderingFor(sort)
  const currentFolderId = folderPath.crumbs[folderPath.crumbs.length - 1]?.id ?? null
  const isSearching = !!search.trim()
  const hasListing = !NAVS_WITHOUT_LISTING.includes(nav)

  const scope = useMemo(() => {
    const term = search.trim() || undefined
    const scopedToFolder = nav === "folders"
    const folderFilter = scopedToFolder
      ? currentFolderId
        ? { folder: currentFolderId }
        : { folder__isnull: true }
      : {}
    return { term, scopedToFolder, folderFilter }
  }, [search, nav, currentFolderId])

  const load = useCallback(async () => {
    const id = ++requestId.current
    setIsLoading(true)

    const { term, scopedToFolder, folderFilter } = scope
    const termParam = term ? { q: term } : {}
    const folderLevel = term
      ? null
      : scopedToFolder
        ? folderKey(currentFolderId)
        : ROOT_FOLDER_KEY
    const workspaceParam = projectId ? { project: projectId } : { location: "company" as const }
    const documentWorkspaceParam = projectId
      ? { lead: projectId }
      : { location: "company" as const }

    const [attachmentsRes, documentsRes, foldersRes, albumsRes] =
      await Promise.all([
        getAttachments({
          ...workspaceParam,
          // Album dates arrive to the minute, so this order is what tells
          // same-minute uploads apart.
          ordering: "-created",
          ...termParam,
          ...folderFilter,
          ...attachmentFilterParams(filters)
        }),
        getProjectDocuments({
          ...documentWorkspaceParam,
          page: 1,
          page_size: DOCUMENT_PAGE_SIZE,
          ordering: documentOrdering,
          ...termParam,
          ...folderFilter,
          ...documentFilterParams(filters)
        }),
        listEveryFolder({
          ...workspaceParam,
          // Newest first: folders the client sort cannot tell apart keep this order.
          ordering: "-created",
          ...(folderLevel ? { parent: folderLevel } : { q: term }),
          ...containerFilterParams(filters)
        }),
        // Albums have no company-scope equivalent yet (a later phase).
        projectId && (nav === "all" || term)
          ? getAlbums({
              project: projectId,
              ...termParam,
              page_size: term ? undefined : ALBUMS_PREVIEW_LIMIT,
              ...containerFilterParams(filters)
            })
          : Promise.resolve(null)
      ])

    if (id !== requestId.current) return

    if (attachmentsRes.ok && attachmentsRes.data) {
      setAttachments(
        Array.isArray(attachmentsRes.data)
          ? attachmentsRes.data
          : attachmentsRes.data.results
      )
    }
    if (documentsRes.ok && documentsRes.data) {
      setDocuments(documentsRes.data.results || [])
      setDocumentsTotal(documentsRes.data.count ?? 0)
      setDocumentsPage(1)
    }
    if (foldersRes.ok && foldersRes.data) {
      setFolders(foldersRes.data.results || [])
    }
    if (albumsRes?.ok && albumsRes.data) {
      setAlbumsPreview(albumsRes.data.results || [])
    }

    setListedFolderKey(folderLevel)
    setIsLoading(false)
    setHasLoaded(true)
  }, [projectId, nav, currentFolderId, scope, filters, documentOrdering])

  const loadMoreDocuments = useCallback(async () => {
    if (documents.length >= documentsTotal) return
    const id = requestId.current
    const nextPage = documentsPage + 1
    setIsLoadingMore(true)

    const response = await getProjectDocuments({
      ...(projectId ? { lead: projectId } : { location: "company" as const }),
      page: nextPage,
      page_size: DOCUMENT_PAGE_SIZE,
      ordering: documentOrdering,
      ...(scope.term ? { q: scope.term } : {}),
      ...scope.folderFilter,
      ...documentFilterParams(filters)
    })

    if (id !== requestId.current) return
    setIsLoadingMore(false)
    if (!response.ok || !response.data) return

    setDocuments((current) => [...current, ...(response.data?.results ?? [])])
    setDocumentsTotal(response.data.count ?? 0)
    setDocumentsPage(nextPage)
  }, [
    projectId,
    documents.length,
    documentsTotal,
    documentsPage,
    scope,
    filters,
    documentOrdering
  ])

  useEffect(() => {
    if (!hasListing) return
    void load()
  }, [load, hasListing])

  // No endpoint answers "attachments in album X" as a filter param — the
  // album's own attachment list is the only source for that membership.
  useEffect(() => {
    if (!filters.album) {
      setAlbumAttachmentIds(null)
      return
    }
    let cancelled = false
    void getAlbum(filters.album).then((response) => {
      if (cancelled || !response.ok || !response.data) return
      setAlbumAttachmentIds(
        new Set((response.data.attachments ?? []).map((a) => a.id))
      )
    })
    return () => {
      cancelled = true
    }
  }, [filters.album])

  // `folders` above holds one level only; the descendant walk needs the whole flat list.
  const [foldersForFilter, setFoldersForFilter] = useState<FolderListItem[]>([])

  useEffect(() => {
    if (!filters.folder || !filters.folder_include_subfolders || !projectId) {
      setFoldersForFilter([])
      return
    }
    let cancelled = false
    void listEveryFolder({ project: projectId }).then((response) => {
      if (cancelled || !response.ok || !response.data) return
      setFoldersForFilter(response.data.results || [])
    })
    return () => {
      cancelled = true
    }
  }, [filters.folder, filters.folder_include_subfolders, projectId])

  const fileItems = useMemo<FileItem[]>(() => {
    // The exact-folder case is already applied server-side (see
    // `sharedFilterParams`) — only the subtree expansion still needs a
    // client-side membership check, since no endpoint takes a folder id set.
    const folderIds =
      filters.folder && filters.folder_include_subfolders
        ? collectFolderIds(filters.folder, foldersForFilter, true)
        : null
    const ownerMatches = (name: string | null | undefined) =>
      !filters.owner || name === filters.owner

    return [
      ...attachments
        .filter(
          (attachment) =>
            ownerMatches(attachment.created_by_name) &&
            (!filters.album || albumAttachmentIds?.has(attachment.id)) &&
            attachmentMatchesFilters(attachment, filters, { folderIds })
        )
        .map((attachment) => mapAttachmentToFileItem(attachment, rights)),
      ...documents
        .filter((document) => documentMatchesFilters(document, filters, { folderIds }))
        .map((document) => mapDocumentToFileItem(document, rights))
    ]
  }, [attachments, documents, filters, rights, albumAttachmentIds, foldersForFilter])

  const unloadedDocuments = Math.max(documentsTotal - documents.length, 0)

  const setNav = useCallback((next: FilesNav) => {
    setNavState(next)
    setFolderPath(ROOT_FOLDER_PATH)
  }, [])

  const openFolder = useCallback((folder: FolderListItem) => {
    setNavState("folders")
    setSearch("")
    setFolderPath((path) => openFolderPath(path, folder))
  }, [])

  const jumpToFolderDepth = useCallback((depth: number) => {
    setFolderPath((path) => truncateFolderPath(path, depth))
  }, [])

  const { pendingParentId } = folderPath
  useEffect(() => {
    if (!pendingParentId) return
    let cancelled = false
    void resolveFolderAncestry(pendingParentId).then((ancestry) => {
      if (!cancelled) setFolderPath((path) => withAncestors(path, ancestry))
    })
    return () => {
      cancelled = true
    }
  }, [pendingParentId])

  return {
    nav,
    setNav,
    folderPath: folderPath.crumbs,
    isFolderPathComplete: folderPath.isComplete,
    currentFolderId,
    isOpeningFolder:
      nav === "folders" &&
      hasLoaded &&
      !isSearching &&
      listedFolderKey !== folderKey(currentFolderId),
    openFolder,
    jumpToFolderDepth,
    search,
    setSearch,
    isSearching,
    filters,
    setFilters,
    hasActiveFilter: hasActiveFilter(filters),
    fileItems,
    folders,
    albumsPreview,
    isLoading,
    isLoadingMore,
    hasLoaded,
    hasListing,
    reload: load,
    loadMoreDocuments,
    hasMore: unloadedDocuments > 0 && documentsCanMatchFilters(filters),
    loadedTotal: fileItems.length,
    overallTotal: fileItems.length + unloadedDocuments
  }
}
